import { useState, useEffect } from "react";
import useSWR from "swr";
import { useDebouncedValue } from "@mantine/hooks";
import { Combobox, useCombobox, InputBase, CloseButton, Group, Stack, Text } from "@mantine/core";
import { useFetch, getStudentLookup } from "../api";
import { formatFullName, extractLogin } from "../utils";

export interface StudentSelectProps {
  label: string;
  description?: string;
  placeholder?: string;
  value: string;
  onChange: (email: string) => void;
}

export function StudentSelect({ label, description, placeholder = "Фамилия Имя", value, onChange }: StudentSelectProps) {
  const { fetch } = useFetch();
  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => { combobox.resetSelectedOption(); setSearch(""); },
  });

  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 250);
  const term = debounced.trim();

  const { data: results } = useSWR(term ? ["students/lookup", term] : null, () => getStudentLookup({ search: term, limit: 20, fetch }));
  const { data: resolved } = useSWR(value ? ["students/lookup", value] : null, () => getStudentLookup({ search: value, limit: 5, fetch }));

  const selected = value ? resolved?.find(s => s.phystech_email === value) ?? null : null;
  const display = selected ? formatFullName(selected) : value;

  return (
    <Combobox store={combobox} onOptionSubmit={email => { onChange(email); combobox.closeDropdown(); }}>
      <Combobox.Target>
        <InputBase
          label={label} description={description} value={display} placeholder={placeholder} readOnly pointer
          onClick={() => combobox.toggleDropdown()}
          rightSection={value ? <CloseButton size="sm" onMouseDown={e => e.preventDefault()} onClick={e => { e.stopPropagation(); onChange(""); }} aria-label="Очистить" /> : <Combobox.Chevron />}
          rightSectionPointerEvents={value ? "all" : "none"}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search value={search} onChange={e => setSearch(e.currentTarget.value)} placeholder="Поиск по фамилии и имени" />
        <Combobox.Options style={{ maxHeight: 240, overflowY: "auto" }}>
          {results?.length ? (
            results.map(s => (
              <Combobox.Option value={s.phystech_email} key={s.phystech_email} active={s.phystech_email === value}>
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text fz="sm">{formatFullName(s)}</Text>
                  <Text fz="xs" c="dimmed">{s.phystech_email}</Text>
                </Group>
              </Combobox.Option>
            ))
          ) : <Combobox.Empty>{term ? "Не найдено" : "Начните вводить имя"}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

/** `stacked` puts the label above and the matched login below, like a regular
 * form field; the default is the publish form's row with the label on the left. */
export function StaffSelect({ label, value, studentId, onChange, warnings, stacked = false }: {
  label: string; value: string; studentId: string | null; onChange: (name: string, id: string | null) => void; warnings?: string[]; stacked?: boolean;
}) {
  const { fetch } = useFetch();
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const term = debounced.trim();
  const { data: students } = useSWR(term ? ["students/lookup", term] : null, () => getStudentLookup({ search: term, limit: 20, fetch }));
  const matchedLogin = studentId ? extractLogin(studentId) : null;

  const inline = !stacked;
  const select = (
    <Combobox store={combobox} onOptionSubmit={email => {
      const s = (students ?? []).find(x => x.phystech_email === email);
      if (s) onChange(formatFullName(s), s.phystech_email);
      combobox.closeDropdown();
    }}>
      <Combobox.Target>
        <InputBase
          style={inline ? { flex: 1 } : undefined} value={value}
          label={stacked ? label : undefined}
          description={stacked ? (studentId ? studentId : value.trim() ? "Не найден среди студентов: запишется только ФИО" : undefined) : undefined}
          inputWrapperOrder={["label", "input", "description"]}
          onChange={e => { onChange(e.currentTarget.value, null); combobox.openDropdown(); }}
          onClick={() => combobox.openDropdown()} onFocus={() => combobox.openDropdown()} onBlur={() => combobox.closeDropdown()}
          placeholder="Фамилия Имя"
          rightSection={inline && matchedLogin ? <Text fz="xs" c="dimmed" pr={6}>{matchedLogin}</Text> : null}
          rightSectionWidth={inline && matchedLogin ? Math.max(48, matchedLogin.length * 8) : undefined}
          rightSectionPointerEvents="none"
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options style={{ maxHeight: 240, overflowY: "auto" }}>
          {students?.length ? (
            students.map(s => (
              <Combobox.Option value={s.phystech_email} key={s.phystech_email}>
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text fz="sm">{formatFullName(s)}</Text>
                  <Text fz="xs" c="dimmed">{extractLogin(s.phystech_email)}</Text>
                </Group>
              </Combobox.Option>
            ))
          ) : <Combobox.Empty>{term ? "Не найдено" : "Начните вводить имя или логин"}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );

  if (stacked) return select;
  return (
    <Stack gap={4}>
      <Group gap="md" wrap="nowrap" align="center">
        <Text w={180} fw={600} c={value ? undefined : "dimmed"} style={{ flexShrink: 0 }}>{label}</Text>
        {select}
      </Group>
      {warnings?.map((msg, i) => <Text key={i} c="yellow.7" fz="sm" pl={196}>⚠ {msg}</Text>)}
    </Stack>
  );
}
